import { z } from 'zod';
const specialtyCodeInput = z
    .string()
    .trim()
    .min(1)
    .transform((value) => value.toLowerCase());
const roleCodeInput = z
    .string()
    .trim()
    .min(1)
    .transform((value) => value.toLowerCase());
function uniqueNormalizedArray(element, minLength = 1) {
    return z
        .array(element)
        .min(minLength)
        .refine((values) => new Set(values).size === values.length);
}
const uniqueSpecialtyCodeArray = uniqueNormalizedArray(specialtyCodeInput);
const uniqueRoleCodeArray = uniqueNormalizedArray(roleCodeInput);
function contributionCapsHaveUniqueRoles(caps) {
    if (!caps?.length)
        return true;
    const roles = caps.map((cap) => cap.roleCode);
    return new Set(roles).size === roles.length;
}
export const RequirementRuleContributionCapSchema = z
    .object({
    roleCode: roleCodeInput,
    max: z.number().int().nonnegative(),
})
    .strict();
export const SpecialtySetMinimumConfigSchema = z
    .object({
    specialtyCodes: uniqueSpecialtyCodeArray,
    contributionCaps: z.array(RequirementRuleContributionCapSchema).optional(),
})
    .strict()
    .refine((data) => contributionCapsHaveUniqueRoles(data.contributionCaps));
export const SpecialtySetRoleMinimumConfigSchema = z
    .object({
    specialtyCodes: uniqueSpecialtyCodeArray,
    roleCodes: uniqueRoleCodeArray,
})
    .strict();
export const DistinctSpecialtyThresholdMinimumConfigSchema = z
    .object({
    specialtyCodes: uniqueSpecialtyCodeArray,
    perSpecialtyMinimum: z.number().int().min(1),
    roleCodes: uniqueRoleCodeArray,
})
    .strict();
export const TotalMinimumConfigSchema = z
    .object({
    roleCodes: uniqueNormalizedArray(roleCodeInput).optional(),
})
    .strict();
const CONFIG_REQUIRED_RULE_TYPES = new Set([
    'SPECIALTY_SET_MINIMUM',
    'SPECIALTY_SET_ROLE_MINIMUM',
    'DISTINCT_SPECIALTY_THRESHOLD_MINIMUM',
]);
const CONFIG_OPTIONAL_RULE_TYPES = new Set(['TOTAL_MINIMUM']);
export function parseRequirementRuleConfig(ruleType, rawConfig) {
    if (CONFIG_REQUIRED_RULE_TYPES.has(ruleType)) {
        if (rawConfig === null || rawConfig === undefined) {
            return { ok: false };
        }
        if (ruleType === 'SPECIALTY_SET_MINIMUM') {
            const result = SpecialtySetMinimumConfigSchema.safeParse(rawConfig);
            return result.success ? { ok: true, parsed: { ruleType, config: result.data } } : { ok: false };
        }
        if (ruleType === 'SPECIALTY_SET_ROLE_MINIMUM') {
            const result = SpecialtySetRoleMinimumConfigSchema.safeParse(rawConfig);
            return result.success ? { ok: true, parsed: { ruleType, config: result.data } } : { ok: false };
        }
        if (ruleType === 'DISTINCT_SPECIALTY_THRESHOLD_MINIMUM') {
            const result = DistinctSpecialtyThresholdMinimumConfigSchema.safeParse(rawConfig);
            return result.success ? { ok: true, parsed: { ruleType, config: result.data } } : { ok: false };
        }
        return { ok: false };
    }
    if (CONFIG_OPTIONAL_RULE_TYPES.has(ruleType)) {
        if (rawConfig === null || rawConfig === undefined) {
            return { ok: true, parsed: { ruleType: 'TOTAL_MINIMUM', config: null } };
        }
        const result = TotalMinimumConfigSchema.safeParse(rawConfig);
        return result.success ? { ok: true, parsed: { ruleType: 'TOTAL_MINIMUM', config: result.data } } : { ok: false };
    }
    if (rawConfig !== null && rawConfig !== undefined) {
        return { ok: false };
    }
    return { ok: false };
}
/** Validates config when creating/updating rules via the API. */
export function validateCreateRequirementRuleConfig(ruleType, rawConfig) {
    if (CONFIG_REQUIRED_RULE_TYPES.has(ruleType)) {
        const parsed = parseRequirementRuleConfig(ruleType, rawConfig);
        return parsed.ok ? { ok: true } : { ok: false, message: 'Invalid or missing config for this rule type' };
    }
    if (CONFIG_OPTIONAL_RULE_TYPES.has(ruleType)) {
        if (rawConfig === null || rawConfig === undefined) {
            return { ok: true };
        }
        const parsed = parseRequirementRuleConfig(ruleType, rawConfig);
        return parsed.ok ? { ok: true } : { ok: false, message: 'Invalid config for TOTAL_MINIMUM' };
    }
    if (rawConfig !== null && rawConfig !== undefined) {
        return { ok: false, message: 'Config is not supported for this rule type' };
    }
    return { ok: true };
}
